/**
 * Canvas stamp preview for item-a65f63b8bcc1a8.
 * 29×9mm aspect, centered lines, font 2–11, optional border.
 */
(function () {
  const STAMP_RATIO = 29 / 9;
  const FONT_OPTION = "字體";
  const BORDER_OPTION = "文字外框";
  const LINE_OPTION = "內容行數";
  const LINE_VALUE_RE = /^(\d+)行$/;

  /** @type {Record<number, { family: string; weight: string; style: string; skew?: number; stroke?: number; google?: boolean }>} */
  const FONT_MAP = {
    2: { family: '"Times New Roman", Times, serif', weight: "700", style: "normal" },
    3: { family: "Arial, Helvetica, sans-serif", weight: "700", style: "normal" },
    4: { family: '"Times New Roman", Times, serif', weight: "700", style: "italic" },
    5: { family: "Arial, Helvetica, sans-serif", weight: "700", style: "italic" },
    6: { family: '"Archivo Black", Impact, sans-serif', weight: "400", style: "normal", google: true },
    7: { family: 'Oswald, "Arial Narrow", sans-serif', weight: "700", style: "normal", google: true },
    8: { family: '"Archivo Black", Impact, sans-serif', weight: "400", style: "italic", skew: -8, google: true },
    9: { family: '"Great Vibes", cursive', weight: "700", style: "normal", stroke: 0.35, google: true },
    10: { family: '"Dancing Script", cursive', weight: "700", style: "normal", google: true },
    11: { family: '"Pinyon Script", cursive', weight: "700", style: "normal", stroke: 0.35, google: true },
  };

  const GOOGLE_FAMILIES = ["Archivo Black", "Oswald", "Great Vibes", "Dancing Script", "Pinyon Script"];
  let fontsReady = false;

  function parseFontNumber(value) {
    const m = String(value || "").match(/(\d+)/);
    const n = m ? Number(m[1]) : 2;
    return FONT_MAP[n] ? n : 2;
  }

  function parseLineCount(value) {
    const m = String(value || "").trim().match(LINE_VALUE_RE);
    return m ? Number(m[1]) : 1;
  }

  function parseHasBorder(value) {
    const v = String(value || "").trim();
    if (!v) return true;
    if (/沒有|無/.test(v)) return false;
    if (/有外框|有框|^有$/.test(v)) return true;
    return false;
  }

  function getSection(root) {
    return (
      root.closest(".shopify-section") ||
      root.closest("[id^='shopify-section-']") ||
      document
    );
  }

  function readOption(section, label) {
    const variantSelects = section.querySelector("variant-selects");
    if (variantSelects) {
      for (const fieldset of variantSelects.querySelectorAll("fieldset")) {
        const legend = fieldset.querySelector("legend");
        if (!legend || !legend.textContent.includes(label)) continue;
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
        const select = fieldset.querySelector("select");
        if (select) return select.value;
      }
    }
    return null;
  }

  function buildFont(size, spec) {
    return `${spec.style} ${spec.weight} ${size}px ${spec.family}`;
  }

  async function ensureFontsLoaded() {
    if (fontsReady) return;
    const loads = [];
    for (const name of GOOGLE_FAMILIES) {
      loads.push(document.fonts.load(`400 24px "${name}"`).catch(() => {}));
      loads.push(document.fonts.load(`700 24px "${name}"`).catch(() => {}));
    }
    await Promise.all(loads);
    fontsReady = true;
  }

  function measureLineWidth(ctx, text, size, spec) {
    if (!spec.skew) {
      ctx.font = buildFont(size, spec);
      return ctx.measureText(text).width;
    }
    ctx.save();
    ctx.font = buildFont(size, spec);
    const w = ctx.measureText(text).width;
    const skewRad = (spec.skew * Math.PI) / 180;
    const extra = Math.abs(Math.tan(skewRad) * size);
    ctx.restore();
    return w + extra;
  }

  function drawLine(ctx, text, x, y, size, spec) {
    ctx.save();
    ctx.font = buildFont(size, spec);
    ctx.fillStyle = "#111";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    if (spec.skew) {
      ctx.translate(x, y);
      ctx.transform(1, 0, Math.tan((spec.skew * Math.PI) / 180), 1, 0, 0);
      x = 0;
      y = 0;
    }

    if (spec.stroke) {
      ctx.lineWidth = Math.max(0.4, size * 0.018);
      ctx.strokeStyle = "#111";
      ctx.strokeText(text, x, y);
    }
    ctx.fillText(text, x, y);
    ctx.restore();
  }

  function lineGapRatio(lineCount, hasBorder) {
    if (lineCount <= 1) return 1;
    if (hasBorder) return lineCount === 3 ? 0.86 : 0.9;
    return 0.94;
  }

  function textBlockHeight(size, lineCount, gapRatio) {
    if (lineCount <= 1) return size;
    return size + (lineCount - 1) * size * gapRatio;
  }

  function fitFontSize(ctx, lines, innerW, innerH, spec, lineCount, hasBorder) {
    const gapRatio = lineGapRatio(lineCount, hasBorder);
    const minSize = 7;
    let size = innerH * 0.52;

    while (size >= minSize) {
      if (textBlockHeight(size, lineCount, gapRatio) > innerH) {
        size -= 0.5;
        continue;
      }
      let fits = true;
      for (const line of lines) {
        if (measureLineWidth(ctx, line, size, spec) > innerW) {
          fits = false;
          break;
        }
      }
      if (fits) return size;
      size -= 0.5;
    }
    return minSize;
  }

  function collectLines(root, lineCount) {
    const lines = [];
    for (let i = 1; i <= lineCount; i += 1) {
      const input = root.querySelector(`#StampLine${i}-${root.dataset.sectionId}`) ||
        root.querySelector(`[data-stamp-line="${i}"] input`);
      const value = input && !input.disabled ? String(input.value || "").trim() : "";
      lines.push(value);
    }
    return lines;
  }

  function drawPreview(canvas, state) {
    const parent = canvas.parentElement;
    if (!parent) return;

    const displayWidth = parent.clientWidth || 320;
    const displayHeight = displayWidth / STAMP_RATIO;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = Math.round(displayWidth * dpr);
    canvas.height = Math.round(displayHeight * dpr);
    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, displayWidth, displayHeight);

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, displayWidth, displayHeight);

    const borderPad = state.hasBorder ? displayWidth * 0.07 : displayWidth * 0.05;
    const innerW = displayWidth - borderPad * 2;
    const innerH = displayHeight - borderPad * 2;

    if (state.hasBorder) {
      ctx.strokeStyle = "#111";
      ctx.lineWidth = Math.max(1.2, displayWidth * 0.014);
      ctx.strokeRect(borderPad, borderPad, innerW, innerH);
    }

    const textInsetY = state.hasBorder ? innerH * 0.14 : innerH * 0.06;
    const textInsetX = state.hasBorder ? innerW * 0.05 : innerW * 0.03;
    const textW = innerW - textInsetX * 2;
    const textH = innerH - textInsetY * 2;
    const textTop = borderPad + textInsetY;

    const spec = FONT_MAP[state.fontNum] || FONT_MAP[2];
    const activeLines = state.lines.slice(0, state.lineCount).map((l) => l.trim());
    const filled = activeLines.filter(Boolean);

    if (!filled.length) {
      ctx.fillStyle = "#bbb";
      ctx.font = '400 13px Arial, sans-serif';
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("請輸入印章內容以預覽", displayWidth / 2, displayHeight / 2);
      return;
    }

    const lineCount = filled.length;
    const gapRatio = lineGapRatio(lineCount, state.hasBorder);
    const size = fitFontSize(ctx, filled, textW, textH, spec, lineCount, state.hasBorder);
    const lineGap = size * gapRatio;
    const blockHeight = textBlockHeight(size, lineCount, gapRatio);
    let y = textTop + (textH - blockHeight) / 2 + size * 0.5;

    for (const line of activeLines) {
      if (!line) continue;
      drawLine(ctx, line, displayWidth / 2, y, size, spec);
      y += lineGap;
    }
  }

  function bindPreview(root) {
    const canvas = root.querySelector("[data-stamp-canvas]");
    if (!canvas) return;

    const section = getSection(root);

    const render = async () => {
      await ensureFontsLoaded();
      const lineCount = parseLineCount(readOption(section, LINE_OPTION));
      const fontNum = parseFontNumber(readOption(section, FONT_OPTION));
      const hasBorder = parseHasBorder(readOption(section, BORDER_OPTION));
      const lines = collectLines(root, 3);
      drawPreview(canvas, { lineCount, fontNum, hasBorder, lines });
    };

    let timer = null;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        render().catch(() => {});
      }, 60);
    };

    schedule();

    root.addEventListener("input", (e) => {
      if (e.target instanceof HTMLInputElement && e.target.name?.startsWith("properties[")) schedule();
    });

    root.addEventListener("stamp-lines-updated", schedule);

    section.addEventListener("change", (e) => {
      const t = e.target;
      if (t instanceof HTMLInputElement || t instanceof HTMLSelectElement) schedule();
    });

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.variantChange, schedule);
    }

    document.addEventListener("shopify:section:load", (event) => {
      if (event.target && event.target.contains(root)) schedule();
    });

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(schedule);
      const frame = root.querySelector("[data-stamp-preview-frame]");
      if (frame) ro.observe(frame);
    }

    window.addEventListener("resize", schedule);
  }

  function init() {
    document.querySelectorAll("[data-stamp-lines-root]").forEach(bindPreview);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
